import { useEffect, useId, useRef, useState } from "react";

import { Button, CopyButton } from "@don/design-system";

import { ADMIN_COPY, adminNote, GUIDE_URL } from "./copy.js";
import type { AdminState } from "./session.js";

/** Before an attempt, or one of the action-needed states (Outlook design-spec decision 28). */
export type DisclosureKind = "before" | AdminState;

const SMALL = { fontSize: "var(--type-small-size)", lineHeight: "var(--type-small-line)" } as const;

/** The explanation line each kind opens with (§6.1). */
const LINE: Record<DisclosureKind, string> = {
  before: ADMIN_COPY.preAttempt,
  required: ADMIN_COPY.requiredLine,
  awaiting: ADMIN_COPY.waitingLine,
  scopes: ADMIN_COPY.scopesLine,
  blocked: ADMIN_COPY.blockedLine,
};

/**
 * The admin disclosure under the Outlook row (issue 465, Outlook design-spec W4, W6, W7, §4.1,
 * §5.5): a toggle, closed before an attempt and open in the action-needed states, the pattern of
 * the design system's `SourcesList` (the toggle is never unmounted, so focus stays on it). Open,
 * it shows the explanation line, the note read-only with its URLs as plain text (never `<a>`,
 * decision 29 N2) and the Copy control. A refused clipboard leaves the control's words, shows the
 * fallback line, and focuses the note box with its whole text selected, until the disclosure
 * closes. With no `link` (no admin-token key, issue 465) there is no note and no Copy control.
 * `blocked` (issue 468, sign-in rules) shows the guide link to `#sign-in-rules` in place of the
 * note and the Copy control.
 */
export function AdminDisclosure({ kind, link }: { kind: DisclosureKind; link: string | null }) {
  const [open, setOpen] = useState(kind !== "before");
  const [refused, setRefused] = useState(false);
  const note = useRef<HTMLDivElement>(null);
  const id = useId();
  const needed = kind !== "before";

  useEffect(() => {
    if (!refused || !open) return;
    const box = note.current;
    if (box === null) return;
    box.focus();
    const range = document.createRange();
    range.selectNodeContents(box);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  }, [refused, open]);

  const toggle = () => {
    setOpen((was) => !was);
    setRefused(false);
  };
  return (
    <div data-connect-admin={kind} style={{ marginTop: 12 }}>
      <button
        type="button"
        data-admin-toggle=""
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={toggle}
        style={{
          display: "inline-flex", alignItems: "center", gap: 4, minHeight: 28, padding: "0 6px", margin: "0 0 0 -6px",
          border: "none", borderRadius: 6, cursor: "pointer", fontFamily: "var(--font-sans)", ...SMALL, fontWeight: 500,
          background: "var(--admin-toggle-bg, transparent)",
          color: `var(--admin-toggle-ink, ${needed ? "var(--ink-1)" : "var(--ink-2)"})`,
          transitionProperty: "background-color, color", transitionDuration: "var(--dur-fast)",
          transitionTimingFunction: "var(--ease-out)",
        }}
      >
        {needed ? ADMIN_COPY.disclosureNeeded : ADMIN_COPY.disclosureBefore}
        <Chevron open={open} />
      </button>
      {open && (
        <div
          id={id}
          data-connect-admin-panel=""
          style={{
            marginTop: 6, padding: "2px 0 2px 16px", borderLeft: "1px solid var(--line)",
            display: "flex", flexDirection: "column", gap: 12, alignItems: "flex-start",
          }}
        >
          <p data-connect-admin-line="" style={{ ...SMALL, margin: 0, maxWidth: "56ch", color: "var(--ink-2)" }}>
            {LINE[kind]}
          </p>
          {kind === "blocked" && <GuideLink anchor="sign-in-rules" />}
          {kind !== "blocked" && link !== null && (
            <>
              <div
                ref={note}
                data-connect-note=""
                data-selected={refused ? "" : undefined}
                role="textbox"
                aria-readonly="true"
                aria-multiline="true"
                aria-label={ADMIN_COPY.noteLabel}
                tabIndex={-1}
                style={{
                  ...SMALL, width: "100%", boxSizing: "border-box", padding: "12px 14px",
                  borderRadius: "var(--radius-control)", background: "var(--ground)",
                  boxShadow: "inset 0 0 0 1px var(--line)", userSelect: "text",
                  color: refused ? "var(--ink-1)" : "var(--ink-2)",
                  outline: refused ? "2px solid var(--accent)" : "none", outlineOffset: 2,
                }}
              >
                {ADMIN_COPY.noteLead}{" "}
                <span
                  data-connect-note-link=""
                  style={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                >
                  {link}
                </span>
                <span style={{ display: "block", marginTop: 6 }}>{ADMIN_COPY.noteGuide}</span>
              </div>
              <CopyButton
                text={adminNote(link)}
                size="button"
                variant={kind === "before" ? "secondary" : kind === "awaiting" ? "quiet" : "primary"}
                buttonSize={kind === "required" || kind === "scopes" ? "md" : "sm"}
                onRefused={() => setRefused(true)}
                data-connect-admin-copy=""
              >
                {kind === "awaiting" ? ADMIN_COPY.copyAgain : ADMIN_COPY.copy}
              </CopyButton>
              {refused && (
                <p
                  role="alert"
                  data-connect-admin-fallback=""
                  style={{ ...SMALL, margin: 0, display: "flex", gap: 8, alignItems: "flex-start", color: "var(--ink-1)" }}
                >
                  <span style={{ display: "flex", marginTop: 2, color: "var(--ink-2)" }}>
                    <InfoMark />
                  </span>
                  {ADMIN_COPY.fallback}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * "Open the guide for your IT admin" (Outlook design-spec §2.1, W7): a link styled as a primary
 * Button that opens the hosted guide in a new tab (`target=_blank`, `rel=noopener noreferrer`),
 * with an external-link glyph after the words. `anchor` names a section (`sign-in-rules`, the
 * blocked state, issue 468). Built with the note in issue 465, which pins W7.
 */
export function GuideLink({ anchor }: { anchor?: string }) {
  return (
    <Button
      as="a"
      href={anchor === undefined ? GUIDE_URL : `${GUIDE_URL}#${anchor}`}
      newTab
      variant="primary"
      data-connect-admin-guide=""
    >
      {ADMIN_COPY.guide}
      <ExternalMark />
    </Button>
  );
}

/** Lucide's external-link, 14 (§2.1). */
function ExternalMark() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" aria-hidden="true" style={{ flex: "none" }}>
      <path d="M15 3h6v6M10 14 21 3M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" {...stroke} />
    </svg>
  );
}

const stroke = { fill: "none", stroke: "currentColor", strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round" } as const;

/** Lucide's chevron-right, 12, turned 90° while open (§4.1, §7). */
function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width={12} height={12} viewBox="0 0 24 24" aria-hidden="true"
      style={{
        flex: "none", color: "var(--ink-2)", transform: open ? "rotate(90deg)" : "none",
        transition: "transform var(--dur-fast) var(--ease-out)",
      }}
    >
      <path d="m9 18 6-6-6-6" {...stroke} strokeWidth={2} />
    </svg>
  );
}

/** Lucide's info, 16: the approval-required status and the fallback line (§2.1). */
export function InfoMark() {
  return (
    <svg data-glyph="info" width={16} height={16} viewBox="0 0 24 24" aria-hidden="true" style={{ flex: "none" }}>
      <circle cx={12} cy={12} r={9} {...stroke} />
      <path d="M12 16v-4M12 8h.01" {...stroke} />
    </svg>
  );
}

/** The static pending ring, 16: the awaiting status (§2.1; the design system's `PendingGlyph`). */
export function WaitingMark() {
  return (
    <svg data-glyph="pending" width={16} height={16} viewBox="0 0 24 24" aria-hidden="true" style={{ flex: "none" }}>
      <circle cx={12} cy={12} r={8} strokeDasharray="2.6 3.2" {...stroke} />
    </svg>
  );
}
